import { buildAuthHeaders } from "../forum/authHeaders";
import { getCookie } from "../utils/csrf";

const ROOT = "/api/seller-marketplace/hub/";

export class SellerHubApiError extends Error {
  constructor(message, status = 0, details = null) {
    super(message);
    this.name = "SellerHubApiError";
    this.status = status;
    this.details = details;
  }
}

async function request(path, { method = "GET", json, form, signal, responseType = "json" } = {}) {
  const upper = method.toUpperCase();
  const csrf = !["GET", "HEAD", "OPTIONS"].includes(upper) ? getCookie("csrftoken") : "";
  const response = await fetch(`${ROOT}${path}`, {
    method: upper,
    credentials: "include",
    signal,
    headers: buildAuthHeaders({
      Accept: responseType === "blob" ? "*/*" : "application/json",
      ...(json !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(csrf ? { "X-CSRFToken": csrf } : {}),
    }),
    ...(json !== undefined ? { body: JSON.stringify(json) } : {}),
    ...(form ? { body: form } : {}),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    const detail = typeof payload?.detail === "string"
      ? payload.detail
      : Object.values(payload || {}).flat().filter(Boolean).join(" ");
    throw new SellerHubApiError(detail || "Cette action est momentanément indisponible.", response.status, payload);
  }
  if (response.status === 204) return {};
  return responseType === "blob" ? response.blob() : response.json();
}

export const loadPracticalHub = (seller, signal) => request(`practical/?seller=${encodeURIComponent(seller)}`, { signal });
export const runPracticalAction = (action, json) => request(`practical/actions/${encodeURIComponent(action)}/`, { method: "POST", json });
export const loadPreparedReplies = () => request("practical/replies/");
export function uploadPracticalDocument(orderId, kind, file) {
  const form = new FormData();
  form.append("order_id", orderId); form.append("kind", kind); form.append("file", file);
  return request("practical/actions/upload_document/", { method: "POST", form });
}
export function uploadPrivateChatDocument(recipient, file) {
  const form = new FormData();
  form.append("recipient", recipient); form.append("file", file);
  return request("practical/chat-documents/", { method: "POST", form });
}
export async function downloadPracticalFile(url, filename) {
  // Never send account credentials to an arbitrary message/link destination.
  if (!url.startsWith(`${ROOT}practical/`) || url.includes("..")) throw new Error("Lien de document invalide.");
  const blob = await request(url.slice(ROOT.length), { responseType: "blob" });
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl; anchor.download = filename; anchor.click();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

export function loadSellerHub(username, filters = {}, signal) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== "") params.set(key, value);
  });
  const query = params.toString();
  return request(`shops/${encodeURIComponent(username)}/${query ? `?${query}` : ""}`, { signal });
}

export function updateSellerShop(data) {
  return request("shop/", { method: "PUT", json: data });
}

export function runListingAction(listingId, action, data = {}) {
  return request(`listings/${encodeURIComponent(listingId)}/actions/${encodeURIComponent(action)}/`, {
    method: "POST",
    json: data,
  });
}

export function runShopAction(username, action, data = {}) {
  return request(`shops/${encodeURIComponent(username)}/actions/${encodeURIComponent(action)}/`, {
    method: "POST",
    json: data,
  });
}

export async function createReturnCase(data, photos = []) {
  const form = new FormData();
  Object.entries(data || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    form.append(key, typeof value === "object" ? JSON.stringify(value) : String(value));
  });
  Array.from(photos || []).slice(0, 6).forEach((photo) => form.append("photos", photo));
  const csrf = getCookie("csrftoken");
  const response = await fetch(`${ROOT}returns/`, {
    method: "POST",
    credentials: "include",
    headers: buildAuthHeaders({ Accept: "application/json", ...(csrf ? { "X-CSRFToken": csrf } : {}) }),
    body: form,
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new SellerHubApiError(payload?.detail || Object.values(payload || {}).flat().join(" ") || "Demande de retour impossible.", response.status, payload);
  return payload;
}

export function runReturnAction(publicId, action, data = {}) {
  return request(`returns/${encodeURIComponent(publicId)}/actions/${encodeURIComponent(action)}/`, {
    method: "POST",
    json: data,
  });
}

export function saveProductPassport(listingId, data = {}) {
  return request(`passports/listings/${encodeURIComponent(listingId)}/`, { method: "PUT", json: data });
}

export function createSellerRecord(kind, data = {}) {
  return request(`records/${encodeURIComponent(kind)}/`, { method: "POST", json: data });
}

export function updateSellerRecord(id, data = {}) {
  return request(`records/item/${encodeURIComponent(id)}/`, { method: "PATCH", json: data });
}

export function deleteSellerRecord(id) {
  return request(`records/item/${encodeURIComponent(id)}/`, { method: "DELETE" });
}

export function compareSellerHub(data) {
  return request("compare/", { method: "POST", json: data });
}

export function importSellerCsv(csv) {
  return request("import/", { method: "POST", json: { csv } });
}

export async function exportSellerCsv() {
  const blob = await request("export.csv", { responseType: "blob" });
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.download = "mes-annonces-eternua.csv";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(href);
}

export async function exportSellerAccountingCsv(ownerUsername = "") {
  const suffix = ownerUsername ? `?owner=${encodeURIComponent(ownerUsername)}` : "";
  const blob = await request(`accounting.csv${suffix}`, { responseType: "blob" });
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.download = "comptabilite-boutique-eternua.csv";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(href);
}

export async function runShipmentAction(orderId, action, data = {}) {
  const csrf = getCookie("csrftoken");
  const response = await fetch(`/api/seller-marketplace/orders/${encodeURIComponent(orderId)}/shipment/${encodeURIComponent(action)}/`, {
    method: "POST",
    credentials: "include",
    headers: buildAuthHeaders({
      "Content-Type": "application/json",
      "Idempotency-Key": window.crypto?.randomUUID?.() || `shipment-${Date.now()}-eternua`,
      ...(csrf ? { "X-CSRFToken": csrf } : {}),
    }),
    body: JSON.stringify(data),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new SellerHubApiError(payload?.detail || "Action logistique impossible.", response.status, payload);
  return payload;
}

export async function replyToVerifiedReview(reviewId, reply) {
  const csrf = getCookie("csrftoken");
  const response = await fetch(`/api/trust/reviews/${encodeURIComponent(reviewId)}/reply/`, {
    method: "POST",
    credentials: "include",
    headers: buildAuthHeaders({ "Content-Type": "application/json", ...(csrf ? { "X-CSRFToken": csrf } : {}) }),
    body: JSON.stringify({ reply }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new SellerHubApiError(payload?.detail || "Réponse impossible.", response.status, payload);
  return payload;
}

export function loadSellerNextGen(seller, signal) {
  const query = seller ? `?seller=${encodeURIComponent(seller)}` : "";
  return request(`nextgen/${query}`, { signal });
}

export function runSellerNextGen(action, seller, data = {}) {
  return request(`nextgen/actions/${encodeURIComponent(action)}/`, {
    method: "POST",
    json: { ...data, ...(seller ? { seller } : {}) },
  });
}

export async function runSellerVisualSearch(seller, photo) {
  const form = new FormData();
  form.append("photo", photo);
  if (seller) form.append("seller", seller);
  const csrf = getCookie("csrftoken");
  const response = await fetch(`${ROOT}nextgen/actions/visual_search/`, {
    method: "POST",
    credentials: "include",
    headers: buildAuthHeaders({ Accept: "application/json", ...(csrf ? { "X-CSRFToken": csrf } : {}) }),
    body: form,
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new SellerHubApiError(payload?.detail || "Recherche visuelle impossible.", response.status, payload);
  return payload;
}

export function exportSellerFiscalReport(seller, year) {
  const params = new URLSearchParams({ seller, year: String(year) });
  window.open(`${ROOT}nextgen/fiscal.csv?${params.toString()}`, "_blank", "noopener,noreferrer");
}
